import { useState } from "react";
import { BOOK_BLURBS, BOOK_LABELS, type Book } from "../types/api";

// Choosing and showing the reconciliation BOOK (AP / AR).
//
// Two surfaces, one vocabulary:
//   BookSwitch — a segmented control in the header, so which book you are in is visible
//                at all times rather than something you have to remember. Shown ONLY to
//                an account holding more than one book, so a single-book user's header
//                is untouched.
//   BookChoice — the first screen of a session for a multi-book account, because there
//                is no safe default: silently opening in AP would let someone reconcile
//                a whole month against the wrong book's layouts.
//
// Both are presentational. They decide nothing and persist nothing; App owns the book.

/** The header switch. `guarded` means a finished reconciliation is on screen, so the
 *  switch asks before discarding it (the result cannot survive — its figures, layouts
 *  and stores are all book-scoped). */
export function BookSwitch({ books, book, guarded, onPick }: {
  books: Book[];
  book: Book;
  guarded: boolean;
  onPick: (b: Book) => void;
}) {
  // The book the user clicked while a result was on screen, awaiting confirmation.
  const [pending, setPending] = useState<Book | null>(null);

  function click(b: Book) {
    if (b === book) return;
    if (guarded) { setPending(b); return; }
    onPick(b);
  }

  return (
    <div className="book-switch-wrap">
      <div className="book-switch" role="radiogroup" aria-label="Reconciliation book">
        {books.map((b) => (
          <button
            key={b}
            type="button"
            role="radio"
            aria-checked={b === book}
            data-book={b}
            className={`book-opt ${b === book ? "active" : ""}`}
            title={`${BOOK_LABELS[b]} — ${BOOK_BLURBS[b]}`}
            onClick={() => click(b)}
          >
            <span className="book-dot" aria-hidden="true" />
            {b}
          </button>
        ))}
      </div>
      {pending && (
        <div className="book-confirm" role="dialog" aria-label="Confirm book switch">
          <p className="book-confirm-title">Switch to {BOOK_LABELS[pending]}?</p>
          <p className="muted book-confirm-body">
            The reconciliation on screen will be closed. You would need to upload the
            files again to come back to it.
          </p>
          <div className="book-confirm-row">
            <button type="button" className="btn" onClick={() => setPending(null)}>
              Stay in {book}
            </button>
            <button type="button" className="btn btn-primary"
              onClick={() => { const b = pending; setPending(null); onPick(b); }}>
              Switch to {pending}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** The first-run chooser. `counts` is how many insurers are onboarded per book, shown
 *  when it has loaded — a book with none onboarded says so here rather than letting the
 *  user discover it on an empty upload form. undefined = still loading, so the cards
 *  stay clickable and the line simply stays blank. */
export function BookChoice({ books, counts, onPick }: {
  books: Book[];
  counts?: Partial<Record<Book, number>>;
  onPick: (b: Book) => void;
}) {
  return (
    <div className="book-choice">
      <div className="book-choice-panel">
        <h1 className="book-choice-title">Which reconciliation?</h1>
        <p className="muted book-choice-sub">
          Your account works on both books. Pick the one you are reconciling — you can
          switch from the header at any time.
        </p>
        <div className="book-choice-grid">
          {books.map((b) => {
            const n = counts?.[b];
            return (
              <button key={b} type="button" className="book-card" data-book={b}
                onClick={() => onPick(b)}>
                <span className="book-card-code" aria-hidden="true">{b}</span>
                <span className="book-card-title">{BOOK_LABELS[b]}</span>
                <span className="book-card-blurb">{BOOK_BLURBS[b]}</span>
                <span className="book-card-meta">
                  {n === undefined ? " "
                    : n === 0 ? "No insurers onboarded yet"
                      : `${n} insurer${n === 1 ? "" : "s"} onboarded`}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
